import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["New", "Emailed", "Closed"];

const STATUS_STYLES = {
  New: "bg-gold/15 text-gold-hover border-gold/50",
  Emailed: "bg-navy/5 text-navy border-navy/30",
  Closed: "bg-green-900/5 text-green-900 border-green-900/30",
};

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
};

const inputClass =
  "w-full bg-transparent border-b border-navy/25 focus:border-gold-hover outline-none py-3 text-navy placeholder:text-muted-foreground/60 transition-colors duration-300";

const LoginCard = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      onLogin(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-navy min-h-[70vh] flex items-center py-24" data-testid="admin-login">
      <div className="max-w-md w-full mx-auto px-6">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4 text-center">Admin Access</p>
          <h1 className="font-serif font-light text-cream text-4xl md:text-5xl tracking-tight text-center">
            Sign in to your <em className="italic text-gold">dashboard.</em>
          </h1>
          <form onSubmit={handleSubmit} className="bg-card border border-navy/10 p-8 md:p-10 mt-10" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="text-xs uppercase tracking-[0.2em] font-bold text-navy">Email</label>
              <input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} autoComplete="username" />
            </div>
            <div className="mt-8">
              <label htmlFor="admin-password" className="text-xs uppercase tracking-[0.2em] font-bold text-navy">Password</label>
              <input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} autoComplete="current-password" />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-800 bg-red-50 border border-red-200 px-4 py-3 mt-6">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="w-full mt-8 rounded-full bg-navy text-cream text-sm font-bold px-8 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

const TeamPanel = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/users`, { withCredentials: true });
      setUsers(data);
    } catch {
      toast.error("Could not load team members.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const createUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/auth/users`, form, { withCredentials: true });
      toast.success(`${form.name} can now sign in.`);
      setForm({ name: "", email: "", password: "", role: "staff" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email})? They will no longer be able to sign in.`)) return;
    try {
      await axios.delete(`${API}/auth/users/${u.id}`, { withCredentials: true });
      toast.success(`${u.name} removed.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="mt-10 grid lg:grid-cols-[1fr_1.2fr] gap-8 items-start" data-testid="team-panel">
      <form onSubmit={createUser} className="bg-card border border-navy/10 p-8" data-testid="team-add-form">
        <h2 className="font-serif text-2xl md:text-3xl text-navy font-light">Add a team member</h2>
        <div className="mt-6">
          <label htmlFor="team-name" className="text-xs uppercase tracking-[0.2em] font-bold text-navy">Full Name</label>
          <input id="team-name" data-testid="team-name-input" type="text" required value={form.name} onChange={update("name")} className={inputClass} />
        </div>
        <div className="mt-6">
          <label htmlFor="team-email" className="text-xs uppercase tracking-[0.2em] font-bold text-navy">Email</label>
          <input id="team-email" data-testid="team-email-input" type="email" required value={form.email} onChange={update("email")} className={inputClass} />
        </div>
        <div className="mt-6">
          <label htmlFor="team-password" className="text-xs uppercase tracking-[0.2em] font-bold text-navy">Temporary Password</label>
          <input id="team-password" data-testid="team-password-input" type="text" required minLength={8} value={form.password} onChange={update("password")} className={inputClass} placeholder="At least 8 characters" />
        </div>
        <div className="mt-6">
          <label htmlFor="team-role" className="text-xs uppercase tracking-[0.2em] font-bold text-navy">Role</label>
          <select id="team-role" data-testid="team-role-select" value={form.role} onChange={update("role")} className={inputClass}>
            <option value="staff">Staff — view &amp; update requests</option>
            <option value="admin">Admin — also manage the team</option>
          </select>
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="team-add-button"
          className="w-full mt-8 rounded-full bg-navy text-cream text-sm font-bold px-8 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy disabled:opacity-60"
        >
          {saving ? "Adding…" : "Add Team Member"}
        </button>
        <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
          Share the email and temporary password with your assistant. They sign
          in at the same /admin page.
        </p>
      </form>

      <div className="bg-card border border-navy/10" data-testid="team-list">
        <div className="bg-navy text-cream px-5 py-4 text-xs uppercase tracking-[0.15em] font-semibold">
          Team Members ({users.length})
        </div>
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between gap-4 px-5 py-4 border-b border-navy/10 last:border-0" data-testid={`team-row-${u.id}`}>
            <div>
              <p className="font-semibold text-navy text-sm">
                {u.name}
                {u.id === currentUser.id && <span className="text-muted-foreground font-normal"> (you)</span>}
              </p>
              <p className="text-muted-foreground text-xs mt-0.5">{u.email}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`border rounded-full text-xs font-bold uppercase tracking-wider px-3 py-1.5 ${u.role === "admin" ? "bg-gold/15 text-gold-hover border-gold/50" : "bg-navy/5 text-navy border-navy/30"}`}>
                {u.role}
              </span>
              {u.id !== currentUser.id && (
                <button
                  onClick={() => removeUser(u)}
                  data-testid={`team-remove-${u.id}`}
                  className="text-xs font-bold uppercase tracking-wider text-red-800 hover:text-red-600 transition-colors duration-200"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("requests");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/consultations`, { withCredentials: true });
      setRequests(data);
    } catch {
      toast.error("Could not load consultation requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/consultations/${id}`, { status }, { withCredentials: true });
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      onLogout();
    }
  };

  return (
    <section className="py-16 md:py-24 min-h-[70vh]" data-testid="admin-dashboard">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b-2 border-gold/60 pb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold-hover mb-3">Admin Dashboard</p>
            <h1 className="font-serif font-light text-navy text-4xl md:text-5xl tracking-tight">
              Consultation <em className="italic text-gold-hover">requests.</em>
            </h1>
            <p className="text-muted-foreground text-sm mt-3">
              Signed in as {user.email} · {requests.length} request{requests.length === 1 ? "" : "s"}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 rounded-full border border-navy/30 text-navy text-sm font-semibold px-5 py-2.5 transition-colors duration-300 hover:border-gold hover:text-gold-hover"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full bg-navy text-cream text-sm font-semibold px-5 py-2.5 transition-colors duration-300 hover:bg-gold hover:text-navy"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>

        {user.role === "admin" && (
          <div className="flex gap-3 mt-8" data-testid="admin-tabs">
            <button
              onClick={() => setTab("requests")}
              data-testid="tab-requests"
              className={`rounded-full text-sm font-semibold px-5 py-2.5 transition-colors duration-300 ${tab === "requests" ? "bg-navy text-cream" : "border border-navy/30 text-navy hover:border-gold hover:text-gold-hover"}`}
            >
              Requests
            </button>
            <button
              onClick={() => setTab("team")}
              data-testid="tab-team"
              className={`rounded-full text-sm font-semibold px-5 py-2.5 transition-colors duration-300 ${tab === "team" ? "bg-navy text-cream" : "border border-navy/30 text-navy hover:border-gold hover:text-gold-hover"}`}
            >
              Team
            </button>
          </div>
        )}

        {tab === "team" && user.role === "admin" ? (
          <TeamPanel currentUser={user} />
        ) : (
        <div className="mt-10 bg-card border border-navy/10 overflow-x-auto">
          {loading ? (
            <p className="p-10 text-muted-foreground text-sm">Loading requests…</p>
          ) : requests.length === 0 ? (
            <p className="p-10 text-muted-foreground text-sm" data-testid="admin-empty-state">
              No consultation requests yet. New submissions from the website will appear here.
            </p>
          ) : (
            <table className="w-full text-sm min-w-[900px]" data-testid="admin-table">
              <thead>
                <tr className="bg-navy text-cream text-left">
                  <th className="px-5 py-4 text-xs uppercase tracking-[0.15em] font-semibold">Name</th>
                  <th className="px-5 py-4 text-xs uppercase tracking-[0.15em] font-semibold">Email</th>
                  <th className="px-5 py-4 text-xs uppercase tracking-[0.15em] font-semibold">Phone</th>
                  <th className="px-5 py-4 text-xs uppercase tracking-[0.15em] font-semibold">Country</th>
                  <th className="px-5 py-4 text-xs uppercase tracking-[0.15em] font-semibold">Matter</th>
                  <th className="px-5 py-4 text-xs uppercase tracking-[0.15em] font-semibold">Received</th>
                  <th className="px-5 py-4 text-xs uppercase tracking-[0.15em] font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} className="border-b border-navy/10 last:border-0 hover:bg-sand/50 transition-colors duration-200" data-testid={`admin-row-${r.id}`}>
                    <td className="px-5 py-4 font-semibold text-navy whitespace-nowrap">{r.name}</td>
                    <td className="px-5 py-4">
                      <a href={`mailto:${r.email}`} className="text-navy underline decoration-gold/60 hover:text-gold-hover transition-colors duration-200">
                        {r.email}
                      </a>
                    </td>
                    <td className="px-5 py-4 text-muted-foreground whitespace-nowrap">{r.phone}</td>
                    <td className="px-5 py-4 text-muted-foreground whitespace-nowrap">{r.country}</td>
                    <td className="px-5 py-4 text-muted-foreground max-w-[280px]">
                      <span className="block truncate" title={r.message}>{r.message}</span>
                    </td>
                    <td className="px-5 py-4 text-muted-foreground whitespace-nowrap">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                    </td>
                    <td className="px-5 py-4">
                      <select
                        value={r.status}
                        onChange={(e) => updateStatus(r.id, e.target.value)}
                        data-testid={`status-select-${r.id}`}
                        className={`border rounded-full text-xs font-bold uppercase tracking-wider px-3 py-1.5 cursor-pointer outline-none ${STATUS_STYLES[r.status] || STATUS_STYLES.New}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        )}
      </div>
    </section>
  );
};

const Admin = () => {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null));
  }, []);

  if (user === undefined) {
    return (
      <section className="bg-navy min-h-[70vh] flex items-center justify-center">
        <p className="text-cream/60 text-sm">Checking your session…</p>
      </section>
    );
  }

  if (!user) return <LoginCard onLogin={setUser} />;
  return <Dashboard user={user} onLogout={() => setUser(null)} />;
};

export default Admin;
